import { Link } from 'react-router-dom';
import { polyvoxUrl, trackPolyvoxClick } from '../utils/polyvox';

const About = () => {
  return (
    <div className="page-container">
      <Link to="/" className="back-button">← Back to Home</Link>

      <h1>About LangShazam</h1>
      <div className="content-card">
        <section>
          <h3>What LangShazam Does</h3>
          <p>
            LangShazam is a free browser tool that listens to a few seconds of speech and tells you what
            language is being spoken. Whether you overhear a conversation in a café, want to identify a
            podcast in a foreign language, or are trying to figure out which dialect your friend's
            grandmother is speaking — point your phone at the audio and tap the mic. Within seconds, you
            see the detected language, with a live transcript of what was said building up as you speak.
          </p>
        </section>

        <section>
          <h3>Why We Built It</h3>
          <p>
            Existing language identification tools are either buried inside translation apps that demand
            you sign in and pick a source language up-front, or they're academic libraries that nobody
            outside of a research lab can use. We wanted something that works the way Shazam works for
            music: tap one button, and a few seconds later you get an answer. No source
            language to pre-select. No app to install. Detection never requires an account;
            Google sign-in is optional if you want your Langle streak on another phone.
          </p>
        </section>

        <section>
          <h3>How It Works (Under the Hood)</h3>
          <p>
            When you tap the mic, your browser captures audio and streams it over a secure WebSocket to
            our backend. The backend forwards the stream to AssemblyAI's Universal-3.6 Pro real-time
            speech model, which transcribes what you say and identifies the spoken language natively
            from the audio — 32 languages, with a confidence score on every finalized phrase. We send
            that result straight back to your browser.
          </p>
          <p>
            Because the model listens to the audio itself rather than guessing from text, it copes
            with accents, short phrases, and even mid-sentence switches between languages. Every
            phrase carries its own language tag, so the result updates live as you speak.
          </p>
        </section>

        <section>
          <h3>What We Detect</h3>
          <p>
            We are building state-of-the-art language detection. LangShazam currently identifies and
            transcribes 32 languages spanning every inhabited continent — from
            widely-spoken languages like English, Mandarin, and Spanish to Afrikaans, Xhosa, Zulu,
            Galician, and Marathi. You can browse the full list on the{' '}
            <Link to="/languages">Languages page</Link>.
          </p>
        </section>

        <section>
          <h3>Limitations</h3>
          <p>
            LangShazam is a probabilistic tool, not an oracle. It works best with at least a few seconds
            of clear speech in a single language. If two people are talking over each other in different
            languages, or if the speaker switches mid-sentence (code-switching), the result reflects the
            dominant language at the moment of detection. Background noise, music, or very short
            utterances can lower confidence — speak clearly and give it a few seconds to settle.
          </p>
        </section>

        <section>
          <h3>Privacy</h3>
          <p>
            Audio is sent to our servers for transcription and may be retained briefly for quality
            monitoring before being deleted. We do not sell your data, profile you, or use your
            recordings for advertising. The full details are in our{' '}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </section>

        <section>
          <h3>Also from us</h3>
          <p>
            If you like hearing other languages, try{' '}
            <a
              href={polyvoxUrl('about')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackPolyvoxClick('about')}
            >
              Polyvox
            </a>
            : clone a short voice sample and hear it speak 75 languages. Same maker as LangShazam.
          </p>
        </section>

        <section>
          <h3>Get In Touch</h3>
          <p>
            Built by Kaan Karakas. If you have feedback, bug reports, or want to use language detection
            inside your own product, reach out via the <Link to="/contact">Contact page</Link>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default About;
